/**
 * Shared padding convention for every popover-style menu in the app (kebab
 * dropdowns, the profile menu, split-button menus, Select's option list, the
 * Sort/Views toolbar dropdowns, etc.).
 *
 * Before this existed, each menu hand-rolled its own paper/list/item padding
 * and they drifted: MenuButton's Sort dropdown had a roomy, clearly-inset
 * rounded highlight (paper padding + generous item padding), while
 * ActionDropdown's kebab, the sidebar's profile menu, and Select's option
 * list were noticeably tighter — same app, three different paddings for what
 * reads as the same component.
 *
 * Usage: spread `MENU_PAPER_SX` into a MUI `<Menu>`'s `slotProps.paper.sx`
 * (or a styled Paper's sx), and spread `MENU_ITEM_SX` into each `<MenuItem>`'s
 * sx — after your own overrides if you need to change hover/selected colors,
 * since those are a different concern from spacing.
 *
 * EVERY LENGTH HERE IS ABSOLUTE px, NEVER A THEME MULTIPLIER (working-conventions §47).
 * This is a layer-1 constant, so it renders under whichever app's theme mounts it, and
 * the two apps agree on neither scale it could be written against:
 *
 *   spacing  `@repo/ui`/`apps/platform` `4 * factor` (spacing.ts) | `apps/app` MUI's
 *            default `8 * factor` (never overridden). So `p: 3` = 12px in the platform
 *            and 24px in the app.
 *   radius   `@repo/ui` `shape.borderRadius: 4` (shape.ts) | `apps/app`
 *            `shape.borderRadius: buttonRadius` (createMuiTheme.ts), which is the
 *            TENANT's `companies_settings.border_radius` — 8 by default, 0 on a brand
 *            that picked "Sharp", and any value in between. So `borderRadius: 1.5` is
 *            6px here, 12px in the app by default, and 0 for some brands.
 *
 * Both were verified in source before converting; the radius is the worse of the two,
 * because it is not merely a fixed 2x — it varies per brand at runtime, so a menu row's
 * corner would silently track a setting that has nothing to do with menus.
 *
 * The px values below are exactly what the old multipliers already resolved to under the
 * platform theme, so this conversion is a NO-OP there and makes the constants correct in
 * `apps/app` for the first time.
 */

/**
 * The house popover elevation — a soft blue-grey lift, not a neutral drop shadow.
 *
 * Extracted because it was hand-rolled in at least FOUR places (`Sort`,
 * `ActionDropdown`, `@repo/ui` `AutoComplete`, the Help popover) plus two more
 * commented-out copies, which is exactly the drift §36 exists to stop: the sidebar's
 * profile menu ended up as the one popover on white NOT carrying it, and read as a
 * different kind of surface from every other menu in the app.
 *
 * Note it is deliberately NOT `theme.shadows[3]`. MUI's neutral greys read as
 * consumer-SaaS chrome against this console's palette; the 145/158/171 blue-grey is
 * what the rest of the app's popovers actually use.
 */
export const MENU_PAPER_SHADOW =
  'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px'

/**
 * The dropdown paper's SURFACE — fill behaviour, edge, elevation, radius.
 *
 * ## ⚠️ `MENU_PAPER_SX` DOES NOT CARRY ANY OF THIS, and that is the whole trap
 *
 * `MENU_PAPER_SX` above is spacing only (gap, insets, max height). Everything a
 * reader actually SEES as "this is one of our dropdowns" — the killed elevation
 * overlay, the 1px divider edge, the shadow, the 12px radius — lives in `theme.ts`
 * as `MuiMenu.styleOverrides.paper`, and MUI keys `styleOverrides` on the COMPONENT
 * NAME. So it reaches a `<Menu>` and reaches NOTHING ELSE.
 *
 * That has now bitten three times, which is why it is a constant rather than a
 * fourth copy:
 *
 *   `MuiAutocomplete.paper`   patched by name in `theme.ts` (~L200), whose own
 *                             comment states the diagnosis: "is a PAPER, but it is
 *                             not a `MuiMenu`, so the `MuiMenu.paper` override
 *                             above never reached it".
 *   `MetricInputsPopover`     a bare `<Popover>`. Same cause, and the symptom was
 *                             the loud one: `backgroundImage` is MUI's dark-mode
 *                             ELEVATION OVERLAY, a lightening gradient it paints on
 *                             every elevated Paper, so the panel floated pale grey
 *                             against a near-black page while every menu beside it
 *                             sat flat.
 *
 * So: a popover-style surface that is NOT a `<Menu>` spreads THIS as well as
 * `MENU_PAPER_SX`. A `<Menu>` needs neither — it gets the chrome from the theme.
 *
 * ⚠️ `backgroundImage: 'none'` is the load-bearing line and it is NOT cosmetic
 * belt-and-braces. It is the only thing that removes the overlay, it is invisible in
 * light mode (MUI paints the overlay only when `palette.mode === 'dark'`), and a
 * hardcoded dark `backgroundColor` would "fix" the screenshot while breaking light
 * mode. Nothing here sets a fill: `Paper`'s own `background.paper` is already right
 * in both modes once the gradient is off.
 *
 * A function rather than an object because three of the four values read the theme.
 */
export const menuPaperChromeSx = (theme: {
  palette: { divider: string }
  shadows: readonly string[]
}) =>
  ({
    backgroundImage: 'none',
    border: `1px solid ${theme.palette.divider}`,
    // `shadows[3]`, matching `MuiMenu.paper` exactly — a Popover's Paper defaults to
    // elevation 8, which is visibly heavier than every menu it sits beside. See the
    // note in `theme.ts`: if the edge ever needs strengthening, raise the border's
    // alpha rather than the elevation.
    boxShadow: theme.shadows[3],
    // 12px, the house radius shared with Card and the data grid. Contents nest
    // smaller (`MENU_ITEM_SX` is 8): an inner corner wants a smaller radius than the
    // corner containing it.
    borderRadius: '12px',
  }) as const

/** Horizontal + vertical inset from the menu's outer border to its rows —
 * the gap the rounded item highlight sits inside, not the item's own padding. */
export const MENU_PAPER_SX = {
  /**
   * The gap between the trigger and the menu paper — 8px, and it lives HERE rather than at
   * each call site.
   *
   * It used to live nowhere: this constant set no margin, so every menu wrote its own `mt: 1`
   * and the platform's menus therefore hung 4px below their triggers while the app's identical
   * `mt: 1` rendered 8px (MUI's default base, §29). Same source line, two gaps — which is
   * exactly the drift a shared constant exists to prevent, and it could not prevent it while
   * the value was not in it.
   *
   * `'8px'` absolute, never `mt: 2`, for the reason the file header gives: this is layer 1 and
   * a multiplier means two different gaps in the two apps. 8 is also what the menus that were
   * already correct had each independently arrived at — `Select`, `SelectField`, `Combobox`,
   * `AutoComplete` and `filterDropdownPaperSx` all state 8px in their own words. This makes
   * that agreement a single fact instead of five coincidences.
   *
   * A menu that legitimately wants a different gap overrides it AFTER the spread — the sidebar
   * menus do exactly that with `mt: 0`, because they open to the SIDE of their trigger and a
   * downward drop makes no sense there (see `sidebarMenuPlacement`). Spread order is load-
   * bearing at those call sites.
   */
  mt: '8px',

  // 4px, down from 8. The inset is doubled between two adjacent papers, so it is what made the
  // sidebar→menu and menu→submenu CONTENT gaps read as uneven: 12+15=27 one side against
  // 15+12+15=42 the other. Equal paper gaps guarantee unequal content gaps, and the only lever
  // that narrows the difference is the inset itself. At 4px the inset is 9 (1 border + 4 paper +
  // 4 list), so the two content gaps become 21 and 30 — a 9px difference instead of 15.
  //
  // Perfect equality is geometrically impossible: it would need a NEGATIVE paper gap.
  pl: '4px',
  pr: '4px',
  pt: '4px',
  pb: '4px',

  /**
   * The LIST's inset, matched to the paper's.
   *
   * The paper's own four sides were already symmetric, but the row-to-edge distance was not:
   * measured on the Help popover, a row sat 9px from the left edge and 5px from the top. The
   * horizontal 9 is 1 border + 4 paper + 4 list; the vertical 5 is 1 + 4 + **0**, because the
   * list's padding computed to `0px 4px` — MUI's `MuiMenu.list` override (`padding: '4px'`) does
   * not reach a Popover-based menu, and the List lands on a disabled-vertical-padding default.
   *
   * Stating it here fixes it for every popover-style menu at once and makes the first and last row
   * sit the same distance from the paper as the rows do from its sides — 9px on all four.
   */
  '& .MuiMenu-list, & .MuiList-root': { padding: '4px' },

  /**
   * Never taller than the viewport, and scroll rather than overflow it.
   *
   * The Help popover has ten rows and is anchored near the bottom of the sidebar, and on a short
   * window its last row ("Status") was unreachable — not merely clipped: the paper extended past
   * the viewport bottom while `overflow` left it unscrollable, so there was no way to get to the
   * row at all.
   *
   * `100dvh` rather than `100vh` so a mobile browser's collapsing toolbar does not reintroduce the
   * overflow it is meant to prevent. 32px keeps the paper off both edges.
   */
  maxHeight: 'calc(100dvh - 32px)',
  overflowY: 'auto',
} as const

/** Per-row padding + the concentric-nesting radius/gap convention (a slightly
 * smaller radius than the paper's own, so the highlight nests cleanly inside
 * it, plus a small gap between consecutive rows). */
export const MENU_ITEM_SX = {
  p: '12px',
  borderRadius: '6px',
  mb: '4px',
  '&:last-of-type': { mb: 0 },
} as const

/** Same padding convention, tightened for compact/dense menus (e.g. a
 * rows-per-page picker) — half the item padding/gap, same paper inset. */
export const MENU_ITEM_DENSE_SX = {
  p: '6px',
  borderRadius: '6px',
  mb: '2px',
  '&:last-of-type': { mb: 0 },
} as const
